import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { IconButton, InputBase } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import MaterialTable, { Column } from "material-table";
import { useDebounce } from "utils/hooks/useDebounce";
import { useHandleChangeEvent } from "utils/hooks/useHandleChangeEvent";
import tableIcons from "../../components/Table/icons";
import { GlobalContext } from "../../globalContext/GlobalContext";
import { GET_EMPLOYEES, GET_EMPLOYEES_CSV, RoutesList } from "../../router/url";
import { useFetch } from "../../utils/hooks/useFetch";
import { PaginatedData } from "../MarginList/EmployeeList.types";
import * as S from "./Employees.css";
import { downloadFile } from "utils/downloadFile";
import { employeeListCSVAPISortParamsMap } from "./constants";

const sortState = { field: "", direction: "asc" };

const toSearchQuery = (search?: string) => (search?.trim() ? `&search=${encodeURIComponent(search)}` : "");

const Employees = () => {
  const columns: Column<any>[] = [
    {
      field: "name",
      title: "Name",
    },
    {
      field: "surname",
      title: "Surname",
    },
    {
      field: "team",
      title: "Team",
    },
    {
      field: "employedSince",
      title: "Employed Since",
    },
    {
      field: "position",
      title: "Position",
    },
    {
      field: "positionCategory",
      title: "Position Category",
    },
    {
      field: "location",
      title: "Location",
    },
  ];

  const pageSizeOptions = [5, 10, 20, 50, 100, 200, 500, 1000];
  const [currentPage, setCurrentPage] = useState(0);
  const [reportResponse, setReportResponse] = useState<PaginatedData<any>>();
  const [pageSize, setPageSize] = useState(1000);

  const { fromQueryDate, toQueryDate, toggleLoading } = useContext(GlobalContext);

  const [reportData, setReportData] = useState<any[]>([]);
  const [search, setSearch] = useHandleChangeEvent();
  const delayedSearch: string | undefined = useDebounce(search, 800);
  const searchQuery = toSearchQuery(delayedSearch);
  const latestRequest = useRef(0);

  let history = useHistory();

  const fetchReport = useFetch();

  const getReport = useCallback(async () => {
    const request = ++latestRequest.current;
    toggleLoading(true);
    try {
      const response: PaginatedData<any> = await fetchReport(
        "GET",
        `${GET_EMPLOYEES}?page=${currentPage}&size=${pageSize}&active=true${searchQuery}`
      );

      // a newer search or page replaced this request while it was loading
      if (request !== latestRequest.current) {
        return;
      }
      setReportData(response.content);
      setReportResponse(response);
    } catch (e) {
      if (request === latestRequest.current) {
        toast.error("List couldn't be loaded. Try again or contact support!");
      }
    } finally {
      if (request === latestRequest.current) {
        toggleLoading(false);
      }
    }
  }, [currentPage, fetchReport, toggleLoading, pageSize, searchQuery]);

  useEffect(() => {
    getReport();
  }, [getReport, fromQueryDate, toQueryDate]);

  return (
    <>
      <S.TopWrapper>
        <S.SearchPaper>
          <InputBase
            placeholder="Name and/or surname"
            name="search"
            fullWidth={false}
            value={search}
            onChange={setSearch}
          />
          <IconButton type="submit" aria-label="search">
            <SearchIcon />
          </IconButton>
        </S.SearchPaper>
      </S.TopWrapper>

      <MaterialTable
        columns={columns}
        data={reportData}
        icons={tableIcons}
        page={currentPage}
        totalCount={reportResponse?.totalElements}
        onChangePage={setCurrentPage}
        onChangeRowsPerPage={setPageSize}
        options={{
          emptyRowsWhenPaging: false,
          search: false,
          pageSizeOptions,
          pageSize: reportResponse?.size || pageSize,
          exportButton: {
            pdf: false,
            csv: true,
          },
          exportCsv: async (columns, data) => {
            const response = await fetchReport(
              "GET",
              `${GET_EMPLOYEES_CSV}?active=true&sort=${employeeListCSVAPISortParamsMap[sortState.field] || ""},${
                sortState.direction
              }${toSearchQuery(search)}`,
              {},
              undefined,
              "blob"
            );
            downloadFile(response, "Employees");
          },
          rowStyle: (rowData: any, index: number) => ({
            backgroundColor: index % 2 ? "#FFF" : "#EEE",
          }),
        }}
        title={""}
        onOrderChange={(orderBy, orderDirection) => {
          sortState.field = (columns[orderBy]?.field as string) || "";
          sortState.direction = orderDirection;
        }}
        onRowClick={(_, rowData) => {
          if (rowData) {
            history.push(`${RoutesList.EmployeeDetail}/${rowData.uid}`);
          }
        }}
      />
    </>
  );
};

export default Employees;
